import styled from "styled-components/macro";
import Link from "ui/Link";
import { theme } from "ui/theme";
import PolicyLayout, {
  $PageTitle,
  $TextContainer,
  $OverviewText,
  $SubsectionTitle,
  $Paragraph,
  $UnorderedList,
  $UnderlinedText,
  $PrivacyPolicyLink,
  $ThirdPartyServiceProvidersLink,
} from "templates/PolicyLayout";
import { EventName } from "utils/analytics/analyticsConstants";

import StudentsConversing from "images/students-conversing.png";
import StudentsConversing2x from "images/students-conversing@2x.png";
import SingleUnderlineMint from "images/single-underline-mint.png";

const $PageTitleTextDecorator = styled.span`
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0.34rem;
    left: -0.2rem;
    right: -0.8rem;
    height: 1.3rem;
    background-image: url(${SingleUnderlineMint});
    background-repeat: no-repeat;
    background-size: contain;
    z-index: ${theme.zIndexBelow};

    @media screen and (${theme.BreakpointTabletDown}) {
      display: none;
    }
  }
`;

const PageTitle = () => {
  return (
    <$PageTitle>
      <$PageTitleTextDecorator>Student</$PageTitleTextDecorator> Privacy Notice
    </$PageTitle>
  );
};

export default function StudentPrivacyNotice() {
  return (
    <PolicyLayout
      pageTitle={<PageTitle />}
      lastUpdatedDate="March 23, 2023"
      src={StudentsConversing}
      srcSet={StudentsConversing2x}
      alt="a graphic illustration of two students facing each other and conversing"
    >
      <$TextContainer>
        <$OverviewText size="h3">
          <$Paragraph>
            Welcome to Along (
            {/* Per Trust, the along.org link on student privacy notice page should direct to the Along product site */}
            <Link
              analyticsName={EventName.PolicyPages.CLICKED_HOME_LINK}
              href="/"
              opensInNewTab
            >
              along.org
            </Link>
            )! As a student user of Along, we want you to have a clear
            understanding of how your personal information is used, collected
            and protected.
          </$Paragraph>
        </$OverviewText>

        <$SubsectionTitle size="h3">Who Are We?</$SubsectionTitle>

        <$Paragraph>
          Along is offered by a nonprofit organization called Gradient Learning.
          Our goal is to help build strong relationships between teachers and
          their students, and to help students develop key life skills.
        </$Paragraph>
        <$Paragraph>
          We take your privacy seriously. This Privacy Notice for Students helps
          to explain how we use the personal information we collect when you use
          Along. You can learn about our practices in more detail, by reading
          the <$PrivacyPolicyLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          What Is &quot;Personal Information&quot;?
        </$SubsectionTitle>

        <$Paragraph>
          Personal information is information about you that can identify who
          you are, for example, your name and your email address. Personal
          information includes other information that we collect about you to
          help us provide you with Along, such as information about your
          computer or computer network.
        </$Paragraph>
        <$Paragraph>
          It also includes educational information about you that can identify
          you, such as content or online communications you provide, and
          information about you that your teachers or mentors may provide.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          Why Do We Collect Your Personal Information? What Do We Use It For?
        </$SubsectionTitle>

        <$Paragraph>
          In order to provide Along, we need to collect certain personal
          information. We use your personal information for educational purposes
          only as directed by your school and teachers to provide you a more
          personalized learning experience.
        </$Paragraph>
        <$Paragraph>
          We do not use your information to make money or to advertise to you.
        </$Paragraph>

        <$SubsectionTitle size="h3">How Do We Get It?</$SubsectionTitle>

        <$Paragraph>
          We get your personal information from different sources.
        </$Paragraph>
        <$Paragraph>
          <$UnderlinedText>
            Information You or Your School Provide Us Directly.
          </$UnderlinedText>
          &nbsp;Your school or teachers provide us with information about you,
          for example, to set up your account (such as sending your personal
          information from Clever, a third party service, to Along). You also
          provide us with information, such as when you respond to a teacher
          prompt by submitting a written or audio message, photo, or video.
        </$Paragraph>
        <$Paragraph>
          <$UnderlinedText>
            Information We Collect When You Use Along.
          </$UnderlinedText>
          &nbsp;We also collect certain information about the devices (such as
          your computer or phone) and networks you use to connect to Along. We
          also collect information about how you use Along, such as when you
          login or interact with prompts.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          Who Can See Your Information?
        </$SubsectionTitle>

        <$Paragraph>
          We only share your personal information for educational purposes only
          as directed by your school.
        </$Paragraph>
        <p>Your information may be shared:</p>
        <$UnorderedList>
          <li>
            within your school with those who need access (such as teachers or
            administrators);
          </li>
          <li>with your your parent, legal guardian, or other caregiver;</li>
          <li>
            with Third Parties we use to support operating the services, for
            example, by hosting data on servers; or
          </li>
          <li>
            with other third parties if required by law or directed by your
            school.
          </li>
        </$UnorderedList>
        <$Paragraph>
          You can learn more about the third party service providers that we use
          by visiting our Help Center article on&nbsp;
          <$ThirdPartyServiceProvidersLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">
          How Long Do We Keep Your Information?
        </$SubsectionTitle>

        <$Paragraph>
          We only keep it as long as we need it. We will remove information that
          identifies you if your school stops using Along or asks us to delete
          it. We may keep this information if we have consent from your
          parent/caregiver, or if we’re required to keep it.
        </$Paragraph>

        <$SubsectionTitle size="h3">What Do I Need To Do?</$SubsectionTitle>

        <$Paragraph>
          Nothing. We work with your school to make sure we get the information
          that we need for educational purposes. Sometimes we need permission
          from your parents or school, and they can say no. We tell them even
          more about how we use your information in our&nbsp;
          <$PrivacyPolicyLink />.
        </$Paragraph>

        <$SubsectionTitle size="h3">What Are My Rights?</$SubsectionTitle>

        <$Paragraph>
          When it comes to your information, you have the right to know what
          information we have about you, to correct anything that’s wrong, and
          more. Your parents/caregivers and school can help protect your rights.
        </$Paragraph>

        <$SubsectionTitle size="h3">How Can I Find Out More?</$SubsectionTitle>

        <$Paragraph>
          Ask your teacher or parent/caregiver if you have any questions and
          they can help you find the answers.
        </$Paragraph>
      </$TextContainer>
    </PolicyLayout>
  );
}
